import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import Button from "../button";

// Function to get the next Tuesday at 7 PM
function getNextTuesdayAt7PM() {
  const now = new Date();
  const dayOfWeek = now.getDay(); // 0 is Sunday, 1 is Monday, 2 is Tuesday, etc.
  const daysUntilNextTuesday = (dayOfWeek <= 2) ? (2 - dayOfWeek) : (9 - dayOfWeek);
  
  // Create the next Tuesday date
  const nextTuesday = new Date(now);
  nextTuesday.setDate(now.getDate() + daysUntilNextTuesday);
  
  // Set time to 7 PM (19:00)
  nextTuesday.setHours(19, 0, 0, 0);
  
  // If it's Tuesday after 7 PM, add 7 days
  if (dayOfWeek === 2 && now.getHours() >= 19) {
    nextTuesday.setDate(nextTuesday.getDate() + 7);
  }
  
  return nextTuesday;
}

// Calculate time remaining until the target date
function getTimeRemaining(targetDate) {
  const now = new Date();
  const total = targetDate.getTime() - now.getTime();
  
  const seconds = Math.floor((total / 1000) % 60);
  const minutes = Math.floor((total / 1000 / 60) % 60);
  const hours = Math.floor((total / (1000 * 60 * 60)) % 24);
  const days = Math.floor(total / (1000 * 60 * 60 * 24));

  return { days, hours, minutes, seconds, total };
}

const TechCourseBanner = ({ scrollToPayments, btnLabel, discount, title, message, setShowBanner, expandedOnMobile, useCustomLayout = false }) => {
  const [pageWidth, setPageWidth] = useState(window.innerWidth);
  const [clickable, setClickable] = useState(false);
  const [nextTuesday, setNextTuesday] = useState(getNextTuesdayAt7PM());
  const [remainingTime, setRemainingTime] = useState(getTimeRemaining(nextTuesday));

  useEffect(() => {
    const handleResize = () => {
      setPageWidth(window.innerWidth);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  useEffect(() => {
    if (pageWidth > 1000) {
      setClickable(false);
      return;
    }
    if (pageWidth <= 500 && expandedOnMobile) {
      setClickable(false);
      return;
    }
    setClickable(true);
  }, [pageWidth, expandedOnMobile]);

  const handleClick = () => {
    // Use the scrollToPayments function to scroll to the payments section
    // We can pass the first cohort ID or undefined to just scroll to the section
    scrollToPayments();
  };
  useEffect(() => {
    const intervalId = setInterval(() => {
      // Get current time and next Tuesday
      const now = new Date();
      const currentNextTuesday = nextTuesday;
      
      // Check if we've passed the current target Tuesday
      if (now.getTime() >= currentNextTuesday.getTime()) {
        // If we've reached the target time, calculate the next Tuesday
        const newNextTuesday = getNextTuesdayAt7PM();
        setNextTuesday(newNextTuesday);
        setRemainingTime(getTimeRemaining(newNextTuesday));
      } else {
        // Otherwise just update the remaining time
        setRemainingTime(getTimeRemaining(currentNextTuesday));
      }
    }, 1000);
    
    return () => {
      clearInterval(intervalId);
    };
  }, [nextTuesday]);

  // We don't need to hide the banner as it always shows the countdown to next Tuesday

  return (
    <div className={`w-full bg-[#F8D210] border border-black py-2 ${useCustomLayout ? 'md:w-full' : ''}`}>
      <div 
        className={`flex flex-col md:flex-row justify-between items-center mx-auto px-4 ${useCustomLayout ? 'max-w-full xl:max-w-full' : 'max-w-7xl'}`}
        onClick={() => clickable ? handleClick() : null}
      >
        {useCustomLayout ? (
          // Custom layout for tech-payments.js
          <div className="flex items-center mb-4 md:mb-0">
            <div className="flex flex-col mr-6 pr-6">
              <h1 className="text-2xl font-bold">{discount}% OFF</h1>
              <span className="text-xs">on one-time payment</span>
            </div>
            <div className="flex flex-col pr-6 md:flex-row items-center">
              <span className="font-bold text-[14px] mr-2">{title}</span>
            </div>
          </div>
        ) : (
          // Standard layout for other pages
          <div className="flex items-center mb-4 md:mb-0">
            <h1 className="text-3xl font-bold mr-6">{discount}% OFF</h1>
            <div>
              <h1 className="font-bold text-base">{title}</h1>
              <p className="text-sm">{message}</p>
            </div>
          </div>
        )}
        
        <div className="flex items-center mb-4 md:mb-0">
          <div className="flex flex-col items-center mx-2">
            <span className="text-xl font-bold">{remainingTime.days}</span>
            <span className="text-xs">Days</span>
          </div>
          <span className="text-xl">:</span>
          <div className="flex flex-col items-center mx-2">
            <span className="text-xl font-bold">{remainingTime.hours}</span>
            <span className="text-xs">Hours</span>
          </div>
          <span className="text-xl">:</span>
          <div className="flex flex-col items-center mx-2">
            <span className="text-xl font-bold">{remainingTime.minutes}</span>
            <span className="text-xs">Mins</span>
          </div>
          <span className="text-xl">:</span>
          <div className="flex flex-col items-center mx-2">
            <span className="text-xl font-bold">{remainingTime.seconds}</span>
            <span className="text-xs">Seconds</span>
          </div>
        </div>
        
        {message && useCustomLayout ? (
          <div className="pl-6 text-sm">
            <span className="font-bold">Note:</span> 
            {message.replace("Note:", "")}
          </div>
        ) : null}
        
        {btnLabel && !useCustomLayout && (
          <Button 
            onClick={handleClick} 
            className="whitespace-nowrap" 
            variant="primary"
          >
            {btnLabel}
          </Button>
        )}
      </div>
    </div>
  );
};

TechCourseBanner.propTypes = {
  scrollToPayments: PropTypes.func.isRequired,
  btnLabel: PropTypes.string,
  discount: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  message: PropTypes.string.isRequired,
  setShowBanner: PropTypes.func.isRequired,
  expandedOnMobile: PropTypes.bool,
  useCustomLayout: PropTypes.bool
};

export default TechCourseBanner;
